import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Check, XCircle, Loader2, Mail, Ticket, Gift } from "lucide-react";
import CinemaBrand from "@/components/CinemaBrand";

/**
 * Stripe-Payment-Result-Page — nach Redirect von Stripe Checkout.
 * Pollt `/api/payments/status/{session_id}` bis payment_status="paid" (oder Timeout).
 */
export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ loading: true, paid: false, tx: null, error: null });

  useEffect(() => {
    if (!sessionId) return setState({ loading: false, paid: false, error: "Keine Session-ID im URL" });
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 20; // 20 × 1.5 s = 30 s
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ loading: false, paid: true, tx: data });
          return;
        }
        if (data.payment_status === "failed") {
          setState({ loading: false, paid: false, error: "Zahlung fehlgeschlagen" });
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setState({ loading: false, paid: false, tx: data, error: "Zahlung dauert länger als erwartet — bitte E-Mail-Postfach prüfen." });
          return;
        }
        setTimeout(poll, 1500);
      } catch (e) {
        if (cancelled) return;
        setState({ loading: false, paid: false, error: e.response?.data?.detail || "Fehler beim Status-Abruf" });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#0A0806] text-[#F5F0E6] flex items-center justify-center px-4">
      <div className="max-w-lg w-full text-center" data-testid="payment-success-page">
        <div className="mb-6"><CinemaBrand size="sm" /></div>

        {state.loading && (
          <>
            <div className="w-20 h-20 rounded-full bg-[#D4AF37]/15 border-2 border-[#D4AF37] flex items-center justify-center mx-auto mb-6">
              <Loader2 size={40} className="text-[#D4AF37] animate-spin" />
            </div>
            <div className="text-[10px] uppercase tracking-[0.5em] text-[#D4AF37]">Zahlung wird bestätigt</div>
            <h1 className="font-heading text-3xl mt-3">Einen Moment bitte …</h1>
            <p className="text-[#A8A398] mt-3">Wir warten auf die Bestätigung von Stripe.</p>
          </>
        )}

        {!state.loading && state.paid && (
          <>
            <div className="w-24 h-24 rounded-full bg-[#D4AF37]/15 border-2 border-[#D4AF37] flex items-center justify-center mx-auto mb-6">
              <Check size={48} className="text-[#D4AF37]" />
            </div>
            <div className="text-[10px] uppercase tracking-[0.5em] text-[#D4AF37]">Bezahlt</div>
            <h1 className="font-heading text-4xl md:text-5xl mt-3">Danke — der Code ist auf dem Weg!</h1>
            <p className="text-[#A8A398] mt-3 flex items-center justify-center gap-2">
              <Mail size={14} /> Wir haben soeben deine Bestätigung per E-Mail versendet.
            </p>
            {state.tx?.fulfillment && (state.tx.fulfillment.items?.length > 0 || state.tx.fulfillment.code || state.tx.fulfillment.reservation_id) && (
              <div className="mt-8 space-y-3 text-left">
                <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] text-center">
                  {(state.tx.fulfillment.items?.length || 1) > 1 ? `${state.tx.fulfillment.items.length} Codes` : "Deine Bestätigung"}
                </div>
                {(state.tx.fulfillment.items || [state.tx.fulfillment]).map((f, i) => (
                  <div key={i} className="card-lux p-5 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      {f.kind === "voucher" ? <Gift size={22} className="text-[#D4AF37]" /> : <Ticket size={22} className="text-[#D4AF37]" />}
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398]">
                          {f.kind === "voucher" ? `${(f.amount || 0).toFixed(2)} € Gutschein`
                            : f.kind === "pack" ? `${f.ticket_count}er-Kinokarte`
                            : f.kind === "reservation" ? `${f.ticket_count || 0} Ticket(s) · Bestellung ${f.order_number || ""}`
                            : "Bestellung"}
                        </div>
                        {f.kind === "reservation" ? (
                          <div className="mt-1 text-sm text-[#F5F0E6]">Plätze: <span className="font-mono text-[#EEDC82]">{(f.seats || []).join(", ")}</span></div>
                        ) : (
                          <div className="font-mono text-2xl text-[#EEDC82] mt-1 tracking-widest">{f.code}</div>
                        )}
                      </div>
                    </div>
                    {f.kind === "reservation" && f.cancel_token && (
                      <Link to={`/reservierung/${f.cancel_token}`} className="text-[10px] uppercase tracking-widest text-[#D4AF37] underline">
                        Ticket öffnen
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="mt-8 flex gap-3 justify-center">
              <Link to="/" data-testid="payment-back-home" className="py-3 px-6 border border-[#2A2A2A] rounded text-[11px] uppercase tracking-widest hover:border-[#D4AF37]">Startseite</Link>
              <Link to="/shop" data-testid="payment-back-shop" className="py-3 px-6 bg-[#D4AF37] text-black rounded text-[11px] uppercase tracking-widest hover:bg-[#EEDC82]">Weiter einkaufen</Link>
            </div>
          </>
        )}

        {!state.loading && !state.paid && (
          <>
            <div className="w-20 h-20 rounded-full bg-[#7A1921]/20 border-2 border-[#7A1921] flex items-center justify-center mx-auto mb-6">
              <XCircle size={40} className="text-[#EE9CA1]" />
            </div>
            <div className="text-[10px] uppercase tracking-[0.5em] text-[#EE9CA1]">Hinweis</div>
            <h1 className="font-heading text-3xl mt-3">{state.error || "Zahlung noch nicht abgeschlossen"}</h1>
            <p className="text-[#A8A398] mt-3">Falls Geld abgebucht wurde, meldet sich Stripe automatisch — und du bekommst deinen Code per E-Mail. Bei Fragen erreichst du uns telefonisch.</p>
            <div className="mt-8 flex gap-3 justify-center">
              <Link to="/shop" className="py-3 px-6 border border-[#2A2A2A] rounded text-[11px] uppercase tracking-widest hover:border-[#D4AF37]">Zurück zum Shop</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-[#0A0806] text-[#F5F0E6] flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center" data-testid="payment-cancel-page">
        <div className="mb-6"><CinemaBrand size="sm" /></div>
        <div className="w-20 h-20 rounded-full border-2 border-[#A8A398] flex items-center justify-center mx-auto mb-6">
          <XCircle size={40} className="text-[#A8A398]" />
        </div>
        <div className="text-[10px] uppercase tracking-[0.5em] text-[#A8A398]">Abgebrochen</div>
        <h1 className="font-heading text-4xl mt-3">Zahlung nicht ausgeführt</h1>
        <p className="text-[#A8A398] mt-3">Kein Problem — dein Warenkorb ist noch da. Du kannst gerne an der Kasse zahlen oder es später online erneut versuchen.</p>
        <div className="mt-8 flex gap-3 justify-center">
          <button onClick={() => nav(-1)} className="py-3 px-6 border border-[#2A2A2A] rounded text-[11px] uppercase tracking-widest hover:border-[#D4AF37]">Zurück</button>
          <Link to="/shop" className="py-3 px-6 bg-[#D4AF37] text-black rounded text-[11px] uppercase tracking-widest hover:bg-[#EEDC82]">Neu im Shop</Link>
        </div>
      </div>
    </div>
  );
}
